import { format } from "date-fns";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogClose, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useLicenses } from "@/features/licensing/api/fetch-licenses";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicLicense, PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { LicenseElement } from "../../licensing/components/license.element";
import { useCreateOrganisation } from "../api/add-organisation";
import {
  AddOrganisationFormData,
  addOrganisationSchema,
} from "../types/add-organisation-schema";

type AddOrganisationFormProps = {
  onClose: () => void;
  initialData?: AddOrganisationFormData;
};

export function AddOrganisationForm(props: AddOrganisationFormProps) {
  const { onClose } = props;
  const form = useForm<AddOrganisationFormData>({
    resolver: zodResolver(addOrganisationSchema),
    defaultValues: {
      name: "",
      licenseId: "",
      concurrency: undefined,
      replay: undefined,
    },
  });

  const { data: licenses, isSuccess: isSuccessLicenses } = useLicenses();

  const { mutateAsync: createOrganisation, isSuccess } = useCreateOrganisation({
    onSuccess: (result) => {
      queryClient.setQueryData(
        ["organisations"],
        (oldData: PublicOrganisation[] | undefined) => {
          if (!oldData) return oldData;
          return [result, ...oldData];
        },
      );
    },
  });

  async function handleCreateOrganisation(data: AddOrganisationFormData) {
    try {
      await createOrganisation({
        name: data.name,
        licenseId: data.licenseId,
        licenseOverrides: {
          concurrency: data.concurrency,
          replay: data.replay,
          licenseEndDate: format(data.licenseEndDate, "yyyy-MM-dd"),
          isGivenEventAccess: data.isGivenEventAccess,
          storageInGB: data.storageInGB,
        },
      });
      onClose();
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  const licenseId = form.watch("licenseId");

  const selectedLicense: PublicLicense | undefined = isSuccessLicenses
    ? licenses?.find((license) => license.id === licenseId)
    : undefined;

  useEffect(() => {
    if (selectedLicense) {
      form.setValue("concurrency", selectedLicense.concurrency);
      form.setValue("replay", selectedLicense.replay);
      form.setValue("storageInGB", selectedLicense.storageInGB);
      form.setValue("isGivenEventAccess", selectedLicense.isGivenEventAccess);
    }
  }, [selectedLicense, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreateOrganisation)}>
        <DialogBody>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <LicenseElement />

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting || isSuccess}
            isAnimated
          >
            Add Organisation
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
